import React, { useEffect, useMemo, useRef } from 'react';
import $ from 'jquery';
import DataTable from 'datatables.net-react';
import DtCore from 'datatables.net-dt';
import 'datatables.net-dt/css/dataTables.dataTables.min.css';
import './Select2'; // registers the Select2 jQuery plugin
import './datagrid.css';

DataTable.use(DtCore);

/**
 * A DataTables table (search, sort, paging) whose cells are React.
 *
 * DataTables inserts plain cell data as HTML, and some of our data comes from customers, so every
 * cell here is rendered by React (which escapes it). DataTables only gets `value(row)` to sort and
 * search with.
 *
 * columns: [{ title, render(row) -> node, value(row) -> string|number, numeric?, align?, orderable?, className? }]
 *   - `value` defaults to '' (not sortable or searchable) when omitted.
 * rows:    array of row objects. Pass a new array when the data changes.
 * simple:  no search / paging / info, for short lists.
 */
const DataGrid = ({ rows, columns, emptyText = 'No records yet', pageLength = 10, order, simple = false, className = '' }) => {
  // DataTables is configured once, so the cell renderers read the latest columns through a ref
  // and never see stale handlers.
  const latest = useRef(columns);
  latest.current = columns;

  const { dtColumns, slots, options } = useMemo(() => {
    const dtColumns = columns.map((c, i) => ({
      title: c.title,
      data: null,
      type: c.numeric ? 'num' : 'string',
      orderable: c.orderable !== false && !!c.value,
      searchable: !!c.value,
      className: [c.align === 'right' || c.numeric ? 'dt-right' : '', c.className || ''].join(' ').trim(),
      name: String(i),
    }));
    const slots = {};
    columns.forEach((_, i) => {
      // Four parameters on purpose: that is how datatables.net-react recognises a renderer that
      // also receives the data type (display, sort, filter...).
      slots[i] = (cell, type, row, meta) => {
        const col = latest.current[i];
        if (!col) return '';
        if (type === 'display') return col.render(row);
        return col.value ? col.value(row) : '';
      };
    });
    const options = {
      autoWidth: false,
      pageLength,
      lengthMenu: [10, 25, 50, 100],
      order: order || [],
      language: { emptyTable: emptyText, zeroRecords: 'No matching records', search: 'Search', searchPlaceholder: 'Search…' },
      ...(simple ? { paging: false, searching: false, info: false } : {}),
    };
    return { dtColumns, slots, options };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The "entries per page" menu is a plain <select>; give it Select2 like every other dropdown.
  // DataTables listens for a native change event, which Select2 does not send, so forward one.
  const wrap = useRef(null);
  useEffect(() => {
    const el = wrap.current && wrap.current.querySelector('.dt-length select');
    if (simple || !el) return undefined;
    const $el = $(el);
    $el.select2({ width: '84px', minimumResultsForSearch: Infinity });
    const forward = () => el.dispatchEvent(new Event('change', { bubbles: true }));
    $el.on('select2:select', forward);
    return () => {
      $el.off('select2:select', forward);
      if ($el.data('select2')) $el.select2('destroy');
    };
  }, [simple]);

  return (
    <div ref={wrap} className={`dg ${simple ? 'dg-simple' : ''} ${className}`}>
      <DataTable data={rows} columns={dtColumns} slots={slots} options={options} className="dg-table" />
    </div>
  );
};

export default DataGrid;
